import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnFiltersState,
  Column,
  getFilteredRowModel,
  getSortedRowModel,
  OnChangeFn,
  ColumnFilter,
  SortingState,
  HeaderGroup,
  Row,
} from "@tanstack/react-table";
import UpIcon from "../../assets/svg/arrow-up.svg";
import DownIcon from "../../assets/svg/arrow-down.svg";
import {
  Fragment,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import CustomTextInput from "./CustomTextInput";
import { useHistory, useRouteMatch } from "react-router-dom";
import CustomText from "../atomic/CustomText";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import LoadingSvg from "../../assets/svg/LoadingSVG";
import PageBack from "../../assets/svg/PageBack";
import PageForward from "../../assets/svg/PageForward";
import debounce from "lodash/debounce";
import CustomDatePicker from "./CustomDatePicker";
import moment from "moment";
import CustomSelect, { optionType } from "../organism/CustomSelect";
const itemsPerPage = [15, 20];

const Filter = ({ column }: { column: Column<any, unknown> }) => {
  let columData: any = column;
  let placeHolder: string = columData?.columnDef?.meta?.placeholder ?? "Search";
  const isDateFilter =
    (column?.columnDef?.header as any)?.toLowerCase().includes("date") ||
    (column?.columnDef?.header as any)?.toLowerCase().includes("dob");
  const isDropDown = columData?.columnDef?.meta?.dropdownOptions?.length > 0;
  const [selectedOption, setSelectedOption] = useState<optionType>({
    label: "All",
    value: "",
  });
  const columnFilterValue = column.getFilterValue()
    ? String(column.getFilterValue())
    : "";
  const [inputText, setInputText] = useState<string>(columnFilterValue ?? "");
  const debounceSearch = useCallback(
    debounce(column.setFilterValue, 700),
    []
  );
  
  // Sync inputText with column filter value when it changes externally
  useEffect(() => {
    if (columnFilterValue !== inputText) {
      setInputText(columnFilterValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilterValue]);
  
  function handleSearch(e: any) {
    setInputText(e.target.value);
    // if (!showDateInput) {
    debounceSearch(e.target.value);
    // }
  }

  function handleDate(date: any) {
    if (date === null) {
      setInputText("");
      debounceSearch("");
    } else {
      const fomrattedDate = moment(date).format("MM-DD-YYYY");
      setInputText(fomrattedDate);
      debounceSearch(fomrattedDate);
    }
  }

  if (isDropDown) {
    return (
      <CustomSelect
        options={columData?.columnDef?.meta?.dropdownOptions}
        className="!bg-transparent min-w-[125px] !py-1"
        selected={selectedOption}
        setSelected={(option: optionType) => {
          setSelectedOption(option);
          column.setFilterValue(option.value);
        }}
      />
    );
  }

  if (isDateFilter) {
    return (
      <CustomDatePicker
        value={inputText}
        handleDate={handleDate}
        className={"min-w-[200px] -ml-2 hover:border hover:border-[#172B4D]"}
      />
    );
  }

  return (
    <CustomTextInput
      // onChange={(e) => column.setFilterValue(e.target.value)}
      onChange={handleSearch}
      placeholder={placeHolder}
      className="font-normal !min-w-[150px] h-full bg-transparent"
      parentContainerClass="bg-transparent border-b pb-1 w-full"
      // value={columnFilterValue}
      value={inputText}
    />
  );
};

interface DataTableProps {
  columns: any[];
  data: any;
  columnVisibility?: any;
  enableClientSideFiltering: boolean;
  enableClientSideSorting: boolean;
  columnFilters: ColumnFilter[];
  globalFilters?: { [key: string]: any };
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
  tableContainerClass?: string;
  setParams: Function;
  isFetching: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: any;
  rowSelection?: any;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    totalItems: number;
    onNextPage: Function;
    onPreviousPage: Function;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    hideRecordsPerPage?: boolean;
  };
  handleRowClick?: (row: any) => void;
  manualSorting?: {
    sorting: any;
    setSorting: any;
  };
}

function RenderButton({
  type,
  onClick,
  disabled,
}: {
  type: string;
  onClick: any;
  disabled: boolean;
}) {
  if (type === "forward")
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={"mr-2 disabled:cursor-not-allowed "}
      >
        <PageForward stroke={disabled ? "#b1b8c3" : "#172B4D"} />
      </button>
    );
  return (
    <button
      disabled={disabled}
      className={"ml-4 mr-6 disabled:cursor-not-allowed "}
      onClick={onClick}
    >
      <PageBack stroke={disabled ? "#b1b8c3" : "#172B4D"} />
    </button>
  );
}

export function Pagination({
  paginationData,
  handleBack,
  handleNext,
  size,
  submitting,
  handleItemsPerPage,
}: any): JSX.Element {
  const startIndex = (paginationData.pageIndex - 1) * size;
  const endIndex = paginationData.pageIndex * size;
  return (
    <div className="flex items-center flex-wrap xs:justify-start md:justify-end orderTable:mr-[90px] xs:mb-20 md:mr-20 my-3 ">
      {submitting === true && (
        <LoadingSvg isSecondary className="animate-spin mr-3" />
      )}
      {paginationData.hideRecordsPerPage ? null : (
        <div className="xs:mr-2 md:mx-2 flex justify-between items-center ">
          <p className="text-xs text-textSecondaryGray xs:mr-2 md:mx-2">
            Records Per Page
          </p>
          <div className="!m-0 !p-0 xs:flex block border rounded-md overflow-hidden">
            {itemsPerPage.map((itemSize) => (
              <button
                key={itemSize}
                type="button"
                onClick={() => handleItemsPerPage(itemSize)}
                className={`${
                  size == itemSize
                    ? "bg-primary text-white"
                    : "bg-white text-[#707070]"
                } ${
                  itemSize !== 50 ? "border-r" : ""
                } w-[28px] h-[28px] inline-block`}
              >
                <p className="text-xs ">{itemSize}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="!text-xs text-royalBlue">
          {paginationData.pageIndex === 1 ? startIndex + 1 : startIndex}-
          {endIndex > paginationData.totalItems
            ? paginationData.totalItems
            : endIndex}{" "}
          <span> of </span> <span>{paginationData.totalItems}</span>
        </p>
        <RenderButton
          type="back"
          onClick={handleBack}
          disabled={
            submitting ||
            paginationData.pageIndex === 1 ||
            paginationData.pageCount === 1
          }
        />
        <RenderButton
          type="forward"
          onClick={handleNext}
          disabled={
            (submitting || paginationData.pageIndex) ===
              Math.round(paginationData.pageCount) ||
            Math.round(paginationData.pageCount) === 0
          }
        />
      </div>
    </div>
  );
}

function TableHeader({
  headerEl,
  rowSelection,
  isEmpty,
}: Readonly<{
  headerEl: HeaderGroup<any>;
  rowSelection?: any;
  isEmpty?: boolean;
}>) {
  const history = useHistory();
  const isUnshippedOrders =
    history.location.pathname.includes("/unshipped-orders");
  const isDocuments = history.location.pathname.includes("/documents");
  const isAssociatedOrders = history.location.pathname.includes(
    `/shipments/active-labels`
  );
  const statusClassName = isUnshippedOrders
    ? "!right-0 lg:!right-[207px]"
    : isDocuments
    ? "!right-0 lg:!right-[351px]"
    : isAssociatedOrders
    ? `right-[328px]`
    : "!right-0 lg:!right-[429px]";

  return (
    <tr>
      {headerEl.headers.map((header, index) => {
        const sticky = (header?.column?.columnDef as any)?.meta?.sticky;
        const stickyClassName = (header?.column?.columnDef as any)?.meta
          ?.stickyClassName;
        const isStatusHeader = header.column.id === "orderStatus";

        return (
          <th
            key={header.id}
            className={`text-left py-3 align-top font-semibold text-royalBlue ${
              rowSelection && "px-7"
            } px-6 text-sm ${
              header.column.getCanSort() ? "cursor-pointer" : ""
            } ${header.id === "Actions" ? "hidden lg:table-cell " : ""} ${
              sticky
                ? `${stickyClassName} ${
                    isEmpty && isStatusHeader
                      ? "!right-[103px]"
                      : isStatusHeader
                      ? statusClassName
                      : ""
                  } bg-[#f9f9fa] z-[40]`
                : ""
            }`}
          >
            <button
              type="button"
              onClick={header.column.getToggleSortingHandler()}
              className="flex items-center"
            >
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
              {!!header.column.getIsSorted() && (
                <img
                  src={
                    (header.column.getIsSorted() as string) === "asc"
                      ? UpIcon
                      : DownIcon
                  }
                  alt="dropdown button"
                  className="inline-block mx-1"
                />
              )}
            </button>

            {header.column.getCanFilter() ? (
              <Filter column={header.column} />
            ) : null}
          </th>
        );
      })}
    </tr>
  );
}

function TableBodyRow({
  row,
  handleRowClick,
  isEmpty,
  rowSelection,
}: {
  readonly row: Row<any>;
  readonly handleRowClick?: any;
  readonly isEmpty?: boolean;
  readonly rowSelection?: any;
}) {
  const history = useHistory();
  const isUnshippedOrders =
    history.location.pathname.includes("/unshipped-orders");
  const isDocuments = history.location.pathname.includes("/documents");
  const isAssociatedOrders = history.location.pathname.includes(
    `/shipments/active-labels`
  );
  const statusClassName = isUnshippedOrders
    ? "!right-0 lg:!right-[207px] min-w-[200px]"
    : isDocuments
    ? "!right-0 lg:!right-[351px] min-w-[200px]"
    : isAssociatedOrders
    ? `right-[328px] min-w-[200px]`
    : "!right-0 lg:!right-[429px] min-w-[200px]";
  return (
    <Fragment>
      <tr
        className={`hover:bg-sideNavGray ${
          rowSelection?.[row.id] ? "bg-sideNavGray" : ""
        } cursor-pointer`}
        onClick={(e) => {
          e.stopPropagation();
          handleRowClick?.(row);
        }}
      >
        {row.getVisibleCells().map((cell, index) => {
          const extraAttributes: any = {};
          const obsecure: boolean = (cell?.column?.columnDef?.meta as any)
            ?.obsecure;
          const sticky = (cell?.column?.columnDef as any)?.meta?.sticky;
          const stickyClassName = (cell?.column?.columnDef as any)?.meta
            ?.stickyClassName;
          const isStatusHeader = cell.column.id === "orderStatus";

          if (obsecure) {
            // extraAttributes["data-openreplay-obscured"] = true;
            extraAttributes["data-dd-privacy"] = "mask";
          }

          return (
            <td
              key={cell.id}
              {...extraAttributes}
              className={`text-left py-3  lg:border-b px-6 text-sm ${
                cell.column.id === "Actions" ? " hidden lg:block" : ""
              } ${
                sticky
                  ? `${stickyClassName} ${
                      isEmpty && isStatusHeader
                        ? "right-[103px]"
                        : isStatusHeader
                        ? statusClassName
                        : ""
                    } bg-white z-[40]`
                  : ""
              }`}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
      <tr className="lg:hidden">
        {row
          .getVisibleCells()
          .filter((cell) => cell.column.id === "Actions")
          .map((cell, index) => {
            return (
              <td
                key={cell.id}
                colSpan={row.getVisibleCells().length}
                className={`text-left py-3 px-6 text-sm border-b`}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
      </tr>
    </Fragment>
  );
}

function DataTable({
  columns,
  data,
  columnVisibility,
  tableContainerClass = "",
  enableClientSideFiltering = false,
  enableClientSideSorting = false,
  columnFilters,
  setColumnFilters,
  globalFilters,
  setParams,
  pagination,
  isFetching,
  enableRowSelection,
  onRowSelectionChange,
  rowSelection,
  handleRowClick,
  manualSorting,
}: Readonly<DataTableProps>): JSX.Element {
  const [clientSideSorting, setClientSideSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      columnVisibility,
      pagination,
      rowSelection,
      sorting: manualSorting?.sorting ?? clientSideSorting,
    },
    onColumnFiltersChange: setColumnFilters,
    pageCount: pagination ? pagination.pageCount : -1,
    getCoreRowModel: getCoreRowModel(),
    // For client side filtering
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnFilters: enableClientSideFiltering,
    enableSorting: enableClientSideSorting || !!manualSorting?.sorting,
    manualPagination: pagination ? true : false,
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: onRowSelectionChange,
    manualSorting: !!manualSorting,
    onSortingChange: !!manualSorting?.setSorting
      ? manualSorting.setSorting
      : setClientSideSorting,
  });
  const history = useHistory();
  const tableRef = useRef<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Mouse Drag Handlers
  const handleMouseDown = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartX(e.pageX - tableRef?.current?.offsetLeft);
    setScrollLeft(tableRef?.current?.scrollLeft);
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const x = e.pageX - tableRef?.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust speed
    tableRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const isEmpty = data.length === 0;

  useEffect(() => {
    const params = new URLSearchParams();
    if (globalFilters) {
      Object.keys(globalFilters).forEach((key: string) => {
        if (Array.isArray(globalFilters[key])) {
          globalFilters[key].forEach((item: any) => {
            params.append(`${key}[]`, item?.value);
          });
        } else if ((globalFilters[key] !== null && globalFilters[key] !== undefined && globalFilters[key] !== "" && globalFilters[key] !== false) || globalFilters[key] === 0) {
          params.append(key, globalFilters[key]);
        }
      });
    }

    if (pagination) {
      params.append("page", pagination.pageIndex.toString());
      params.append("size", pagination.pageSize.toString());
    }

    columnFilters.forEach((filter: any) => {
      // Skip patientDOB if it's already in globalFilters to avoid duplicates
      if (filter.id === "patientDOB" && globalFilters?.patientDOB) {
        return;
      }
      params.append(filter.id, filter.value);
    });

    history.location.search = params.toString();

    window.history.replaceState(
      null,
      "",
      `${history.location.pathname}${
        !!history.location.search ? `?${history.location.search}` : ""
      }`
    );
    setParams(`${!!params.toString() ? `?${params.toString()}` : ""}`);
  }, [
    columnFilters,
    pagination?.pageIndex,
    pagination?.pageCount,
    globalFilters,
    manualSorting?.sorting,
  ]);

  return (
    <Fragment>
      <div
        // ref={tableRef}
        // onMouseDown={handleMouseDown}
        // onMouseMove={handleMouseMove}
        // onMouseUp={handleMouseUp}
        // onMouseLeave={handleMouseUp}
        className={`${tableContainerClass} cursor-grab`}
      >
        <table
          className={`${
            tableContainerClass.includes("border")
              ? "border-r border-l"
              : "border"
          } table-auto w-full`}
        >
          <thead className="bg-sideNavGray sticky top-0 border-b z-[50]">
            {table.getHeaderGroups().map((headerEl) => (
              <TableHeader
                headerEl={headerEl}
                key={headerEl.id}
                rowSelection={rowSelection}
                isEmpty={isEmpty}
              />
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
     <tr>
  <td
    // data-openreplay-obscured
    data-dd-privacy="mask"
    colSpan={columns.length}
    className="border-none p-0"
  >
    <div className="sticky left-0 w-screen max-w-full  flex justify-center h-[40vh] items-center">
      <CustomText
        text={
          columnFilters.length === 0
            ? "No data found"
            : "No data against search found"
        }
        fontWeight="font-semibold"
        fontSize="text-lg"
        className="text-center text-royalBlue p-5"
      />
    </div>
  </td>
</tr>
            ) : (
              <Fragment>
                {table.getRowModel().rows.map((row) => (
                  <TableBodyRow
                    row={row}
                    handleRowClick={isDragging ? () => null : handleRowClick}
                    rowSelection={rowSelection}
                    key={row.id}
                    isEmpty={isEmpty}
                  />
                ))}
              </Fragment>
            )}
          </tbody>
        </table>
        {isFetching && (
          <div
            className="top-0 bottom-0 left-0 right-0 w-full h-full absolute"
            style={{ zIndex: 2000 /*  backgroundColor: 'rgba(0,0,0,0.2)' */ }}
          >
            <div className={`flex w-full h-full justify-center items-center`}>
              <div className="animate-spin">
                <img
                  src={require("../../assets/images/loadingPNG.png")}
                  className={"h-[50px] w-[50px]"}
                  alt="loading indicator"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {pagination && (
        <Pagination
          paginationData={pagination}
          handleBack={() => {
            pagination?.onPreviousPage?.();
          }}
          handleNext={() => {
            pagination?.onNextPage();
          }}
          size={pagination.pageSize}
          handleItemsPerPage={(size: number) => pagination.setPageSize(size)}
          submitting={false}
        />
      )}
    </Fragment>
  );
}

export default DataTable;
